import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { bulkIndexingService } from '../api/bulkIndexingService';
import { profileService, Profile } from '../api/profileService';
import { bulkIndexingWebSocket } from '../services/bulkIndexingWebSocket';
import { logger } from '../utils/logger';
import { getErrorMessage } from '../types/errors';
import { ConfirmationModal } from '../components/ConfirmationModal';
import {
  BulkIndexingJob,
  JobStatus,
  TargetType,
  WebSocketMessage,
  JobFilter,
  EntityOption,
} from '../types/bulkIndexing';

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  margin-bottom: 10px;
  color: #333;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  margin-bottom: 20px;
  font-size: 18px;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #2196f3;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-size: 14px;
  color: #555;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  ${(props) => {
    switch (props.$variant) {
      case 'danger':
        return `
          background-color: #f44336;
          color: white;
          &:hover { background-color: #d32f2f; }
        `;
      case 'secondary':
        return `
          background-color: #9e9e9e;
          color: white;
          &:hover { background-color: #757575; }
        `;
      default:
        return `
          background-color: #2196f3;
          color: white;
          &:hover { background-color: #1976d2; }
        `;
    }
  }}

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const SYSTEM_DEFAULT_PROMPT_PROFILE_ID = 'default';
const USE_DEFAULT_PROMPT_OPTION = '__use_default_prompt__';
const DEFAULT_PROMPT_LABEL = 'Use default prompt profile (system default)';

const normalizeStoredProfileId = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  if (
    value === SYSTEM_DEFAULT_PROMPT_PROFILE_ID ||
    value === USE_DEFAULT_PROMPT_OPTION
  ) {
    return USE_DEFAULT_PROMPT_OPTION;
  }
  return value;
};

const toStorageProfileId = (value: string): string => {
  return value === USE_DEFAULT_PROMPT_OPTION
    ? SYSTEM_DEFAULT_PROMPT_PROFILE_ID
    : value;
};

const resolveProfileSelectValue = (profile: Profile): string => {
  const profileId = profile.profile_id || profile.id;
  if (profileId === SYSTEM_DEFAULT_PROMPT_PROFILE_ID) {
    return USE_DEFAULT_PROMPT_OPTION;
  }
  return profileId;
};

const Alert = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  
  ${(props) => {
    switch (props.$type) {
      case 'success':
        return `
          background-color: #e8f5e9;
          color: #2e7d32;
          border-left: 4px solid #4caf50;
        `;
      case 'error':
        return `
          background-color: #ffebee;
          color: #c62828;
          border-left: 4px solid #f44336;
        `;
      default:
        return `
          background-color: #e3f2fd;
          color: #1565c0;
          border-left: 4px solid #2196f3;
        `;
    }
  }}
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const InputWithError = styled(Input)<{ $hasError?: boolean }>`
  ${(props) =>
    props.$hasError &&
    `
    border-color: #f44336;
    &:focus {
      border-color: #f44336;
    }
  `}
`;

const JobList = styled.div`
  margin-top: 20px;
`;

const JobCard = styled.div<{ $status: JobStatus }>`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${(props) => {
    switch (props.$status) {
      case 'completed': return '#4caf50';
      case 'failed': return '#f44336';
      case 'cancelled':
      case 'stopped': return '#ff9800';
      case 'processing':
      case 'started': return '#2196f3';
      case 'queued': return '#9c27b0';
      default: return '#9e9e9e';
    }
  }};
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const JobTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #333;
`;

const StatusBadge = styled.span<{ $status: JobStatus }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${(props) => {
    switch (props.$status) {
      case 'completed':
        return 'background: #e8f5e9; color: #2e7d32;';
      case 'failed':
        return 'background: #ffebee; color: #c62828;';
      case 'cancelled':
      case 'stopped':
        return 'background: #fff3e0; color: #e65100;';
      case 'processing':
      case 'started':
        return 'background: #e3f2fd; color: #1565c0;';
      case 'queued':
        return 'background: #f3e5f5; color: #7b1fa2;';
      default:
        return 'background: #f5f5f5; color: #757575;';
    }
  }}
`;

const JobDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;

const JobDetail = styled.div`
  font-size: 13px;
`;

const JobDetailLabel = styled.div`
  color: #888;
  margin-bottom: 4px;
`;

const JobDetailValue = styled.div`
  color: #333;
  font-weight: 500;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin: 12px 0;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #2196f3, #21cbf3);
  width: ${(props) => props.$progress}%;
  transition: width 0.3s ease;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterSelect = styled(Select)`
  width: auto;
  min-width: 150px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ConnectionStatus = styled.div<{ $connected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${(props) => (props.$connected ? '#4caf50' : '#f44336')};
  margin-bottom: 16px;
`;

const StatusDot = styled.div<{ $connected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.$connected ? '#4caf50' : '#f44336')};
`;

// Main Component
export const BulkIndexingManagementPage: React.FC = () => {
  // Form state
  const [targetType, setTargetType] = useState<'full' | 'account' | 'workspace' | 'project'>('full');
  const [targetId, setTargetId] = useState('');
  const [enableAiProcessing, setEnableAiProcessing] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [batchSize, setBatchSize] = useState(100);
  const [forceReindex, setForceReindex] = useState(false);

  const defaultProfileInfo = profiles.find(
    (profile) =>
      (profile.profile_id || profile.id) === SYSTEM_DEFAULT_PROMPT_PROFILE_ID
  );
  const defaultProfileLabel = defaultProfileInfo
    ? `Use default prompt profile (${defaultProfileInfo.name})`
    : DEFAULT_PROMPT_LABEL;
  const otherProfiles = profiles.filter(
    (profile) =>
      (profile.profile_id || profile.id) !== SYSTEM_DEFAULT_PROMPT_PROFILE_ID
  );

  // Job state
  const [jobs, setJobs] = useState<BulkIndexingJob[]>([]);
  const [activeJobs, setActiveJobs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<JobStatus | ''>('');
  const [filterTargetType, setFilterTargetType] = useState<TargetType | ''>('');

  // UI state
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    jobId: string | null;
    isLoading: boolean;
  }>({
    isOpen: false,
    jobId: null,
    isLoading: false,
  });

  // Form validation state
  const [batchSizeError, setBatchSizeError] = useState<string | null>(null);
  const [targetIdError, setTargetIdError] = useState<string | null>(null);

  // Show alert temporarily
  const showAlert = (type: 'success' | 'error' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Validate batch size (memoized validation cache for performance)
  const batchSizeValidationCache = React.useRef<Map<number, boolean>>(new Map());
  
  const validateBatchSize = React.useCallback((value: number): boolean => {
    // Check cache first
    if (batchSizeValidationCache.current.has(value)) {
      const cached = batchSizeValidationCache.current.get(value)!;
      if (!cached) {
        setBatchSizeError('Batch size must be between 1 and 10,000');
      } else {
        setBatchSizeError(null);
      }
      return cached;
    }
    
    // Perform validation
    const isValid = !isNaN(value) && value >= 1 && value <= 10000;
    batchSizeValidationCache.current.set(value, isValid);
    
    // Limit cache size to prevent memory growth (keep last 100 entries)
    if (batchSizeValidationCache.current.size > 100) {
      const firstKey = batchSizeValidationCache.current.keys().next().value;
      batchSizeValidationCache.current.delete(firstKey);
    }
    
    if (!isValid) {
      setBatchSizeError('Batch size must be between 1 and 10,000');
    } else {
      setBatchSizeError(null);
    }
    return isValid;
  }, []);

  // Validate target ID
  const validateTargetId = (targetType: TargetType, value: string): boolean => {
    if (targetType === 'full') {
      setTargetIdError(null);
      return true;
    }

    if (!value || !value.trim()) {
      setTargetIdError(`${targetType} ID is required`);
      return false;
    }

    // Validate format based on type
    if (targetType === 'project') {
      const projectId = parseInt(value);
      if (isNaN(projectId) || projectId <= 0) {
        setTargetIdError('Project ID must be a positive integer');
        return false;
      }
    } else {
      // Account and workspace: alphanumeric with underscores/hyphens
      const sanitized = value.trim().replace(/[^a-zA-Z0-9_-]/g, '');
      if (sanitized !== value.trim()) {
        setTargetIdError('Target ID contains invalid characters. Use only letters, numbers, underscores, and hyphens');
        return false;
      }
      if (value.length > 100) {
        setTargetIdError('Target ID must be 100 characters or less');
        return false;
      }
    }

    setTargetIdError(null);
    return true;
  };

  // Load jobs
  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bulkIndexingService.listJobs({
        status: filterStatus || undefined,
        target_type: filterTargetType || undefined,
        limit: 50,
      });
      setJobs(response.jobs);

      // Track active jobs (queued, started, and processing are all active)
      const active = new Set<string>();
      response.jobs.forEach((job) => {
        if (job.status === 'queued' || job.status === 'started' || job.status === 'processing') {
          active.add(job.job_id);
        }
      });
      setActiveJobs(active);
    } catch (error) {
      logger.error('Error loading jobs', error);
      showAlert('error', 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterTargetType]);

  // Load profiles when component mounts or when AI processing is enabled
  const loadProfiles = useCallback(async () => {
    if (!enableAiProcessing) {
      return;
    }
    
    try {
      setLoadingProfiles(true);
      const fetchedProfiles = await profileService.getProfiles();
      setProfiles(fetchedProfiles);
      
      // Set default profile if available and none selected
      if (fetchedProfiles.length > 0 && !selectedProfileId) {
        const lastSelectedProfileId = localStorage.getItem('bulk_indexing_last_profile_id');
        const normalizedStoredProfileId = normalizeStoredProfileId(lastSelectedProfileId);
        const lastSelectedProfile = lastSelectedProfileId
          ? fetchedProfiles.find(
              (p) =>
                (p.profile_id === lastSelectedProfileId) ||
                (p.id === lastSelectedProfileId)
            )
          : null;

        const defaultProfile =
          lastSelectedProfile ||
          fetchedProfiles.find((p) => p.is_default) ||
          fetchedProfiles.find(
            (p) =>
              p.profile_id === SYSTEM_DEFAULT_PROMPT_PROFILE_ID ||
              p.id === SYSTEM_DEFAULT_PROMPT_PROFILE_ID
          ) ||
          fetchedProfiles.find((p) => p.name?.toLowerCase().includes('default')) ||
          fetchedProfiles[0];

        const resolvedProfileId =
          normalizedStoredProfileId ||
          (defaultProfile ? resolveProfileSelectValue(defaultProfile) : '');

        if (resolvedProfileId) {
          setSelectedProfileId(resolvedProfileId);
          localStorage.setItem(
            'bulk_indexing_last_profile_id',
            toStorageProfileId(resolvedProfileId)
          );
        }
      }
    } catch (error) {
      logger.error('Error loading profiles', error);
      // Don't show alert - just log, profiles are optional
      if (profiles.length === 0) {
        setProfiles([
          {
            id: 'default',
            profile_id: 'default',
            name: 'Default Profile',
            description: 'Default AI processing profile',
          },
        ]);
        setSelectedProfileId(USE_DEFAULT_PROMPT_OPTION);
        localStorage.setItem(
          'bulk_indexing_last_profile_id',
          SYSTEM_DEFAULT_PROMPT_PROFILE_ID
        );
      }
    } finally {
      setLoadingProfiles(false);
    }
  }, [enableAiProcessing, selectedProfileId, profiles.length]);

  // Load profiles when AI processing is enabled
  useEffect(() => {
    if (enableAiProcessing) {
      loadProfiles();
    } else {
      // Clear selection when AI processing is disabled
      setSelectedProfileId('');
    }
  }, [enableAiProcessing, loadProfiles]);

  // Normalize selected profile ID once real profile metadata has been loaded
  useEffect(() => {
    if (!profiles.length || !selectedProfileId || selectedProfileId === USE_DEFAULT_PROMPT_OPTION) {
      return;
    }

    const matchedProfile = profiles.find((profile) =>
      [profile.profile_id, profile.id, profile.name].filter(Boolean).includes(selectedProfileId)
    );

    if (matchedProfile?.profile_id && matchedProfile.profile_id !== selectedProfileId) {
      setSelectedProfileId(matchedProfile.profile_id);
    }
  }, [profiles, selectedProfileId]);

  // Initial load and periodic refresh (optimized with Page Visibility API)
  useEffect(() => {
    loadJobs();
    
    // Only set up interval if there are active jobs or we're on the page
    let interval: NodeJS.Timeout | null = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - stop refreshing
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      } else {
        // Page is visible - restart refresh if there are active jobs
        if (activeJobs.size > 0 && !interval) {
          interval = setInterval(() => {
            // Only refresh if page is visible and there are active jobs
            if (!document.hidden && activeJobs.size > 0) {
              loadJobs();
            }
          }, 5000); // Refresh every 5 seconds
        }
      }
    };
    
    // Start interval only if there are active jobs
    if (activeJobs.size > 0) {
      interval = setInterval(() => {
        // Only refresh if page is visible and there are active jobs
        if (!document.hidden && activeJobs.size > 0) {
          loadJobs();
        }
      }, 5000);
    }
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadJobs, activeJobs.size]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    // Set up WebSocket handlers
    const unsubscribeConnection = bulkIndexingWebSocket.onConnectionChange((connected) => {
      setWsConnected(connected);
    });

    const unsubscribeMessage = bulkIndexingWebSocket.onMessage((message: WebSocketMessage) => {
      logger.debug('Received WebSocket message', { messageType: message.message_type, jobId: message.job_id });
      
      // Update job in list
      setJobs((prevJobs) => {
        return prevJobs.map((job) => {
          if (job.job_id === message.job_id) {
            // Update job based on message type
            switch (message.message_type) {
              case 'job_created':
                // Job was created and queued - add to active jobs if not already there
                setActiveJobs((prev) => {
                  const next = new Set(prev);
                  next.add(message.job_id);
                  return next;
                });
                return { ...job, status: 'queued' };
              case 'job_progress':
                // Job is processing - update status if it was queued
                const updatedJob = { ...job, progress: message.progress };
                if (job.status === 'queued') {
                  updatedJob.status = 'processing';
                }
                return updatedJob;
              case 'job_completed':
                return { ...job, status: 'completed', progress: message.stats };
              case 'job_failed':
                return { ...job, status: 'failed', error_message: message.error };
              case 'job_cancelled':
                return { ...job, status: 'cancelled' };
              default:
                return job;
            }
          }
          return job;
        });
      });

      // Update active jobs
      if (message.message_type === 'job_completed' || message.message_type === 'job_failed' || message.message_type === 'job_cancelled') {
        setActiveJobs((prev) => {
          const next = new Set(prev);
          next.delete(message.job_id);
          return next;
        });
      }
    });

    return () => {
      // Cleanup: unsubscribe all handlers and disconnect WebSocket
      unsubscribeConnection();
      unsubscribeMessage();
      // Disconnect WebSocket when component unmounts
      bulkIndexingWebSocket.disconnect().catch((error) => {
        logger.error('Error disconnecting WebSocket on unmount', error);
      });
    };
  }, []);

  // Connect to WebSocket for active jobs
  useEffect(() => {
    if (activeJobs.size > 0) {
      const firstActiveJob = Array.from(activeJobs)[0];
      bulkIndexingWebSocket.connect(firstActiveJob).catch((error) => {
        logger.error('Error connecting to WebSocket', error, { jobId: firstActiveJob });
        showAlert('error', 'Failed to connect to real-time updates. Please refresh the page.');
      });
    } else {
      // Disconnect when no active jobs
      bulkIndexingWebSocket.disconnect().catch((error) => {
        logger.error('Error disconnecting WebSocket', error);
      });
    }
  }, [activeJobs]);

  // Start indexing
  const handleStartIndexing = async () => {
    // Validate inputs before submission
    if (!validateBatchSize(batchSize)) {
      showAlert('error', 'Please fix batch size validation errors');
      return;
    }

    if (!validateTargetId(targetType, targetId)) {
      showAlert('error', 'Please fix target ID validation errors');
      return;
    }

    try {
      setSubmitting(true);

      const isDefaultOptionSelected = selectedProfileId === USE_DEFAULT_PROMPT_OPTION;
      const selectedProfile = !isDefaultOptionSelected
        ? profiles.find(
            (profile) =>
              profile.profile_id === selectedProfileId ||
              profile.id === selectedProfileId ||
              profile.name === selectedProfileId
          )
        : undefined;

      const canonicalProfileId = isDefaultOptionSelected
        ? SYSTEM_DEFAULT_PROMPT_PROFILE_ID
        : selectedProfile?.profile_id || selectedProfileId || undefined;

      const request = {
        enable_ai_processing: enableAiProcessing,
        default_profile_id:
          enableAiProcessing && canonicalProfileId ? canonicalProfileId : undefined,
        batch_size: batchSize,
        force_reindex: targetType !== 'full' ? forceReindex : undefined,
      };

      // For project, ensure targetId is converted to number
      let processedTargetId = targetId;
      if (targetType === 'project') {
        processedTargetId = parseInt(targetId).toString();
      }

      let response;
      switch (targetType) {
        case 'full':
          response = await bulkIndexingService.startFullIndexing(request);
          break;
        case 'account':
          response = await bulkIndexingService.startAccountIndexing(processedTargetId, request);
          break;
        case 'workspace':
          response = await bulkIndexingService.startWorkspaceIndexing(processedTargetId, request);
          break;
        case 'project':
          response = await bulkIndexingService.startProjectIndexing(parseInt(processedTargetId), request);
          break;
      }

      // Show appropriate message based on status
      const statusMessage = response.status === 'queued' 
        ? `Indexing job queued: ${response.job_id}`
        : `Indexing job started: ${response.job_id}`;
      showAlert('success', statusMessage);
      
      // Connect to WebSocket for this job
      await bulkIndexingWebSocket.connect(response.job_id);
      
      // Reload jobs to get the latest status
      await loadJobs();
    } catch (error: unknown) {
      logger.error('Error starting indexing', error);
      const errorMessage = getErrorMessage(error);
      showAlert('error', errorMessage || 'Failed to start indexing');
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel job
  const handleCancelJob = (jobId: string) => {
    setConfirmationModal({
      isOpen: true,
      jobId,
      isLoading: false,
    });
  };

  const confirmCancelJob = async () => {
    if (!confirmationModal.jobId) return;

    try {
      setConfirmationModal((prev) => ({ ...prev, isLoading: true }));
      await bulkIndexingService.cancelJob(confirmationModal.jobId);
      showAlert('success', 'Job cancelled successfully');
      setConfirmationModal({ isOpen: false, jobId: null, isLoading: false });
      await loadJobs();
    } catch (error: unknown) {
      logger.error('Error cancelling job', error);
      const errorMessage = getErrorMessage(error);
      showAlert('error', errorMessage || 'Failed to cancel job');
      setConfirmationModal((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const cancelCancelJob = () => {
    if (!confirmationModal.isLoading) {
      setConfirmationModal({ isOpen: false, jobId: null, isLoading: false });
    }
  };

  // Calculate progress percentage (memoized for performance)
  const calculateProgress = React.useCallback((job: BulkIndexingJob): number => {
    if (!job.progress) return 0;
    const total = job.progress.total_assets_found || 0;
    const indexed = job.progress.total_assets_indexed || 0;
    return total > 0 ? Math.round((indexed / total) * 100) : 0;
  }, []);

  // Format date (memoized for performance)
  const formatDate = React.useCallback((dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  }, []);

  // Memoize filtered jobs to avoid recalculating on every render
  const filteredJobs = React.useMemo(() => {
    return jobs.filter((job) => {
      if (filterStatus && job.status !== filterStatus) {
        return false;
      }
      if (filterTargetType && job.target_type !== filterTargetType) {
        return false;
      }
      return true;
    });
  }, [jobs, filterStatus, filterTargetType]);

  return (
    <Container>
      <Header>
        <Title>Bulk Indexing Management</Title>
        <Subtitle>
          Manage and monitor bulk indexing operations for accounts, workspaces, and projects
        </Subtitle>
      </Header>

      {alert && (
        <Alert $type={alert.type}>
          {alert.message}
        </Alert>
      )}

      <Grid>
        {/* Start Indexing Form */}
        <Card>
          <CardTitle>Start New Indexing Job</CardTitle>

          <FormGroup>
            <Label>Target Type</Label>
            <Select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as TargetType)}
            >
              <option value="full">Full Database</option>
              <option value="account">Account</option>
              <option value="workspace">Workspace</option>
              <option value="project">Project</option>
            </Select>
          </FormGroup>

          {targetType !== 'full' && (
            <FormGroup>
              <Label>Target ID</Label>
              <InputWithError
                type="text"
                value={targetId}
                onChange={(e) => {
                  setTargetId(e.target.value);
                  // Clear error on change, validate on blur
                  if (targetIdError) {
                    setTargetIdError(null);
                  }
                }}
                onBlur={() => validateTargetId(targetType, targetId)}
                placeholder={`Enter ${targetType} ID`}
                $hasError={!!targetIdError}
              />
              {targetIdError && <ErrorMessage>{targetIdError}</ErrorMessage>}
            </FormGroup>
          )}

          <FormGroup>
            <Label>Batch Size</Label>
            <InputWithError
              type="number"
              value={batchSize}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 100;
                setBatchSize(value);
                // Clear error on change, validate on blur
                if (batchSizeError) {
                  setBatchSizeError(null);
                }
              }}
              onBlur={() => validateBatchSize(batchSize)}
              min={1}
              max={10000}
              $hasError={!!batchSizeError}
            />
            {batchSizeError && <ErrorMessage>{batchSizeError}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="enableAi"
                checked={enableAiProcessing}
                onChange={(e) => setEnableAiProcessing(e.target.checked)}
              />
              <CheckboxLabel htmlFor="enableAi">
                Enable AI Processing
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>

          {enableAiProcessing && (
          <FormGroup>
            <Label htmlFor="profileSelect">
              AI Profile / Prompt
              {loadingProfiles && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>(Loading...)</span>}
            </Label>
            <Select
              id="profileSelect"
              value={selectedProfileId}
              onChange={(e) => {
                const profileId = e.target.value;
                setSelectedProfileId(profileId);
                if (profileId) {
                  localStorage.setItem(
                    'bulk_indexing_last_profile_id',
                    toStorageProfileId(profileId)
                  );
                }
              }}
              disabled={loadingProfiles}
            >
              <option value="">Select a profile...</option>
              <option value={USE_DEFAULT_PROMPT_OPTION}>{defaultProfileLabel}</option>
              {otherProfiles.map((profile) => {
                const profileValue = profile.profile_id || profile.id;
                return (
                  <option key={profileValue} value={profileValue}>
                    {profile.name}
                    {profile.is_default ? ' (Default)' : ''}
                    {profile.description ? ` - ${profile.description}` : ''}
                  </option>
                );
              })}
            </Select>
            {!otherProfiles.length && !loadingProfiles && (
              <p style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                Profiles will be loaded shortly, but you can use the default profile option above right now.
              </p>
            )}
          </FormGroup>
          )}

          {targetType !== 'full' && (
            <FormGroup>
              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  id="forceReindex"
                  checked={forceReindex}
                  onChange={(e) => setForceReindex(e.target.checked)}
                />
                <CheckboxLabel htmlFor="forceReindex">
                  Force Reindex (Skip Change Detection)
                </CheckboxLabel>
              </CheckboxGroup>
            </FormGroup>
          )}

          <ButtonGroup>
            <Button
              onClick={handleStartIndexing}
              disabled={submitting || (targetType !== 'full' && !targetId)}
            >
              {submitting ? <LoadingSpinner /> : 'Start Indexing'}
            </Button>
          </ButtonGroup>
        </Card>

        {/* Active Jobs Dashboard */}
        <Card>
          <CardTitle>Active Jobs</CardTitle>
          <ConnectionStatus $connected={wsConnected}>
            <StatusDot $connected={wsConnected} />
            {wsConnected ? 'Real-time updates connected' : 'Connecting to real-time updates...'}
          </ConnectionStatus>

          {activeJobs.size === 0 ? (
            <EmptyState>No active jobs</EmptyState>
          ) : (
            <JobList>
              {jobs
                .filter((job) => activeJobs.has(job.job_id))
                .map((job) => (
                  <JobCard key={job.job_id} $status={job.status}>
                    <JobHeader>
                      <JobTitle>
                        {job.target_type.toUpperCase()}
                        {job.target_id && ` - ${job.target_id}`}
                      </JobTitle>
                      <StatusBadge $status={job.status}>{job.status}</StatusBadge>
                    </JobHeader>

                    <JobDetails>
                      <JobDetail>
                        <JobDetailLabel>Job ID</JobDetailLabel>
                        <JobDetailValue>{job.job_id.substring(0, 8)}...</JobDetailValue>
                      </JobDetail>
                      <JobDetail>
                        <JobDetailLabel>Started</JobDetailLabel>
                        <JobDetailValue>{formatDate(job.created_at)}</JobDetailValue>
                      </JobDetail>
                      {job.progress && (
                        <JobDetail>
                          <JobDetailLabel>Progress</JobDetailLabel>
                          <JobDetailValue>
                            {job.progress.total_assets_indexed || 0} /{' '}
                            {job.progress.total_assets_found || 0}
                          </JobDetailValue>
                        </JobDetail>
                      )}
                    </JobDetails>

                    <ProgressBar>
                      <ProgressFill $progress={calculateProgress(job)} />
                    </ProgressBar>

                    <ButtonGroup>
                      <Button
                        $variant="danger"
                        onClick={() => handleCancelJob(job.job_id)}
                      >
                        Cancel Job
                      </Button>
                    </ButtonGroup>
                  </JobCard>
                ))}
            </JobList>
          )}
        </Card>
      </Grid>

      {/* Job History */}
      <Card>
        <CardTitle>Job History</CardTitle>

        <FilterBar>
          <FilterSelect
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as JobStatus | '')}
          >
            <option value="">All Statuses</option>
            <option value="queued">Queued</option>
            <option value="started">Started</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="stopped">Stopped</option>
          </FilterSelect>

          <FilterSelect
            value={filterTargetType}
            onChange={(e) => setFilterTargetType(e.target.value as TargetType | '')}
          >
            <option value="">All Types</option>
            <option value="full">Full Database</option>
            <option value="account">Account</option>
            <option value="workspace">Workspace</option>
            <option value="project">Project</option>
          </FilterSelect>

          <Button $variant="secondary" onClick={loadJobs}>
            {loading ? <LoadingSpinner /> : 'Refresh'}
          </Button>
        </FilterBar>

        {loading && filteredJobs.length === 0 ? (
          <EmptyState>
            <LoadingSpinner /> Loading jobs...
          </EmptyState>
        ) : filteredJobs.length === 0 ? (
          <EmptyState>No jobs found</EmptyState>
        ) : (
          <JobList>
            {filteredJobs
              .filter((job) => !activeJobs.has(job.job_id))
              .map((job) => (
                <JobCard key={job.job_id} $status={job.status}>
                  <JobHeader>
                    <JobTitle>
                      {job.target_type.toUpperCase()}
                      {job.target_id && ` - ${job.target_id}`}
                    </JobTitle>
                    <StatusBadge $status={job.status}>{job.status}</StatusBadge>
                  </JobHeader>

                  <JobDetails>
                    <JobDetail>
                      <JobDetailLabel>Job ID</JobDetailLabel>
                      <JobDetailValue>{job.job_id.substring(0, 8)}...</JobDetailValue>
                    </JobDetail>
                    <JobDetail>
                      <JobDetailLabel>Started</JobDetailLabel>
                      <JobDetailValue>{formatDate(job.created_at)}</JobDetailValue>
                    </JobDetail>
                    {job.completed_at && (
                      <JobDetail>
                        <JobDetailLabel>Completed</JobDetailLabel>
                        <JobDetailValue>{formatDate(job.completed_at)}</JobDetailValue>
                      </JobDetail>
                    )}
                    {job.progress && job.progress.total_assets_indexed !== undefined && (
                      <JobDetail>
                        <JobDetailLabel>Assets Indexed</JobDetailLabel>
                        <JobDetailValue>{job.progress.total_assets_indexed}</JobDetailValue>
                      </JobDetail>
                    )}
                    {job.progress && job.progress.processing_time_seconds !== undefined && (
                      <JobDetail>
                        <JobDetailLabel>Duration</JobDetailLabel>
                        <JobDetailValue>
                          {Math.round(job.progress.processing_time_seconds)}s
                        </JobDetailValue>
                      </JobDetail>
                    )}
                  </JobDetails>

                  {job.error_message && (
                    <Alert $type="error">{job.error_message}</Alert>
                  )}
                </JobCard>
              ))}
          </JobList>
        )}
      </Card>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title="Cancel Job"
        message="Are you sure you want to cancel this job? This action cannot be undone."
        confirmText="Cancel Job"
        cancelText="Keep Job"
        variant="danger"
        onConfirm={confirmCancelJob}
        onCancel={cancelCancelJob}
        isLoading={confirmationModal.isLoading}
      />
    </Container>
  );
};

export default BulkIndexingManagementPage;

